import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { NativeSelect, useConfirm } from "./crm-ui";
import { useCrmText } from "@/lib/crm/i18n";
import { entityLabel, fieldLabel, placeableFields, sectionLabel, type CrmEntity } from "@/lib/crm/fields";
import { crmFetch, type CrmError, type CrmFormResponse, type CrmLayout, type CrmLayoutSection } from "@/lib/crm/api";
import { ArrowDown, ArrowUp, GripVertical, Loader2, Lock, Plus, RotateCcw, Trash2, X } from "lucide-react";

// Fields a record cannot be saved without. A contact needs one of its two e-mails on the form (checked on save).
const LOCKED: Record<CrmEntity, string[]> = { contact: [], lead: ["topic", "contactId"] };

interface Props {
  entity: CrmEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layout: CrmLayout;
  isCustom: boolean;
  onSaved: (form: CrmFormResponse) => void;
}

type Drag = { key: string; from: string | null } | null;

const clone = (l: CrmLayout): CrmLayoutSection[] =>
  l.sections.map((s) => ({ id: s.id, label: s.label ?? null, fields: s.fields.map((f) => ({ key: f.key, required: !!f.required })) }));

/** Admin form designer: reorder and move fields between sections, required flags, section names. */
export function CrmFormDesigner({ entity, open, onOpenChange, layout, isCustom, onSaved }: Props) {
  const { lang, tx } = useCrmText();
  const { toast } = useToast();
  const [confirmDialog, ask] = useConfirm();
  const [sections, setSections] = useState<CrmLayoutSection[]>(() => clone(layout));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const drag = useRef<Drag>(null);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setSections(clone(layout));
      setError(null);
    }
    wasOpen.current = open;
  }, [open, layout]);

  const locked = LOCKED[entity];
  const placed = useMemo(() => new Set(sections.flatMap((s) => s.fields.map((f) => f.key))), [sections]);
  const available = placeableFields(entity).filter((f) => !placed.has(f.key));
  const label = (key: string) => fieldLabel(entity, key, lang);

  const update = (fn: (draft: CrmLayoutSection[]) => void) => {
    setSections((prev) => {
      const next = prev.map((s) => ({ ...s, fields: s.fields.map((f) => ({ ...f })) }));
      fn(next);
      return next;
    });
    setError(null);
  };

  /** Moves (or adds, when it is not on the form) a field to a section, before `beforeKey` or at the end. */
  const moveField = (key: string, toSection: string, beforeKey?: string) => update((next) => {
    let item = { key, required: locked.includes(key) };
    for (const s of next) {
      const idx = s.fields.findIndex((f) => f.key === key);
      if (idx >= 0) { item = s.fields[idx]; s.fields.splice(idx, 1); }
    }
    const target = next.find((s) => s.id === toSection);
    if (!target) return;
    const at = beforeKey ? target.fields.findIndex((f) => f.key === beforeKey) : -1;
    if (at >= 0) target.fields.splice(at, 0, item);
    else target.fields.push(item);
  });

  const removeField = (key: string) => {
    if (locked.includes(key)) return;
    update((next) => { for (const s of next) s.fields = s.fields.filter((f) => f.key !== key); });
  };

  const shift = (sectionId: string, index: number, dir: -1 | 1) => update((next) => {
    const s = next.find((x) => x.id === sectionId);
    if (!s) return;
    const j = index + dir;
    if (j < 0 || j >= s.fields.length) return;
    [s.fields[index], s.fields[j]] = [s.fields[j], s.fields[index]];
  });

  const shiftSection = (index: number, dir: -1 | 1) => update((next) => {
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
  });

  const removeSection = (index: number) => {
    const s = sections[index];
    if (s.fields.some((f) => locked.includes(f.key))) {
      setError(tx("mustKeep", { fields: s.fields.filter((f) => locked.includes(f.key)).map((f) => label(f.key)).join(", ") }));
      return;
    }
    update((next) => { next.splice(index, 1); });
  };

  const addSection = () => {
    if (sections.length >= 12) { setError(tx("maxSections")); return; }
    update((next) => { next.push({ id: `custom_${Date.now().toString(36)}`, label: tx("newSection"), fields: [] }); });
  };

  const validate = (): string | null => {
    if (sections.length === 0) return tx("mustKeep", { fields: (locked.length ? locked : ["firstName", "personalEmail"]).map(label).join(", ") });
    const missing = locked.filter((k) => !placed.has(k));
    if (entity === "contact" && !placed.has("firstName") && !placed.has("lastName")) missing.push("firstName");
    if (entity === "contact" && !placed.has("personalEmail") && !placed.has("corporateEmail")) missing.push("personalEmail");
    if (missing.length) return tx("mustKeep", { fields: missing.map(label).join(", ") });
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setSaving(true);
    try {
      const body: CrmLayout = {
        sections: sections.map((s) => ({
          id: s.id,
          label: s.label && s.label.trim() && s.label.trim() !== sectionLabel(s.id, null, lang, entity) ? s.label.trim() : null,
          fields: s.fields.map((f) => ({ key: f.key, required: locked.includes(f.key) ? true : f.required })),
        })),
      };
      const res = await crmFetch<CrmFormResponse>("PUT", `/api/crm/forms/${entity}`, body);
      onSaved(res);
      toast({ title: tx("formSaved") });
      onOpenChange(false);
    } catch (e) {
      setError((e as CrmError).message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const ok = await ask({ title: tx("resetDefault"), text: tx("designerHint"), confirm: tx("resetDefault"), cancel: tx("cancel"), destructive: true });
    if (!ok) return;
    setSaving(true);
    try {
      const res = await crmFetch<CrmFormResponse>("DELETE", `/api/crm/forms/${entity}`);
      onSaved(res);
      toast({ title: tx("formReset") });
      onOpenChange(false);
    } catch (e) {
      setError((e as CrmError).message);
    } finally {
      setSaving(false);
    }
  };

  // ── Drag and drop (native) ───────────────────────────────────────────────
  const onDragStart = (key: string, from: string | null) => (e: React.DragEvent) => {
    drag.current = { key, from };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };
  const allowDrop = (target: string) => (e: React.DragEvent) => {
    if (!drag.current) return;
    e.preventDefault();
    if (dropTarget !== target) setDropTarget(target);
  };
  const dropOnField = (sectionId: string, beforeKey: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const d = drag.current;
    drag.current = null;
    setDropTarget(null);
    if (d && d.key !== beforeKey) moveField(d.key, sectionId, beforeKey);
  };
  const dropOnSection = (sectionId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const d = drag.current;
    drag.current = null;
    setDropTarget(null);
    if (d) moveField(d.key, sectionId);
  };
  const dropOnAvailable = (e: React.DragEvent) => {
    e.preventDefault();
    const d = drag.current;
    drag.current = null;
    setDropTarget(null);
    if (d && d.from) removeField(d.key);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="form-designer">
        {confirmDialog}
        <DialogHeader>
          <DialogTitle>{tx("designerTitle", { entity: entityLabel(entity, lang) })}</DialogTitle>
          <DialogDescription>{tx("designerHint")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="space-y-3">
            {sections.map((s, si) => (
              <div
                key={s.id}
                className={`rounded-lg border bg-card ${dropTarget === `section:${s.id}` ? "ring-2 ring-ring" : ""}`}
                onDragOver={allowDrop(`section:${s.id}`)}
                onDrop={dropOnSection(s.id)}
                data-testid={`designer-section-${s.id}`}
              >
                <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
                  <Input
                    value={s.label ?? sectionLabel(s.id, null, lang, entity)}
                    placeholder={sectionLabel(s.id, null, lang, entity)}
                    onChange={(e) => update((next) => { next[si].label = e.target.value; })}
                    className="h-8 max-w-xs font-medium"
                    aria-label={tx("sectionName")}
                    maxLength={80}
                    data-testid={`designer-section-label-${s.id}`}
                  />
                  <div className="ml-auto flex items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("moveUp")} onClick={() => shiftSection(si, -1)} disabled={si === 0}>
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("moveDown")} onClick={() => shiftSection(si, 1)} disabled={si === sections.length - 1}>
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title={tx("removeSection")} onClick={() => removeSection(si)} data-testid={`designer-remove-section-${s.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <ul className="space-y-1 p-2">
                  {s.fields.length === 0 && (
                    <li className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">{tx("emptySection")}</li>
                  )}
                  {s.fields.map((f, fi) => {
                    const isLocked = locked.includes(f.key);
                    return (
                      <li
                        key={f.key}
                        draggable
                        onDragStart={onDragStart(f.key, s.id)}
                        onDragEnd={() => { drag.current = null; setDropTarget(null); }}
                        onDragOver={allowDrop(`field:${f.key}`)}
                        onDrop={dropOnField(s.id, f.key)}
                        className={`flex flex-wrap items-center gap-2 rounded-md border bg-background px-2 py-1.5 text-sm ${dropTarget === `field:${f.key}` ? "border-t-2 border-t-ring" : ""}`}
                        data-testid={`designer-field-${f.key}`}
                      >
                        <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">
                          {label(f.key)}
                          {(f.required || isLocked) && <span className="ml-0.5 text-destructive">*</span>}
                        </span>
                        {isLocked && <span title={tx("lockedField")}><Lock className="h-3.5 w-3.5 text-muted-foreground" /></span>}
                        <label className="flex items-center gap-1.5 text-xs">
                          <Checkbox
                            checked={isLocked || f.required}
                            disabled={isLocked}
                            onCheckedChange={(v) => update((next) => { next[si].fields[fi].required = v === true; })}
                            data-testid={`designer-required-${f.key}`}
                          />
                          {tx("required")}
                        </label>
                        <NativeSelect
                          value=""
                          onChange={(e) => { if (e.target.value) moveField(f.key, e.target.value); }}
                          className="h-8 w-36 text-xs"
                          aria-label={tx("moveTo")}
                          data-testid={`designer-move-${f.key}`}
                        >
                          <option value="">{tx("moveTo")}</option>
                          {sections.filter((x) => x.id !== s.id).map((x) => (
                            <option key={x.id} value={x.id}>{sectionLabel(x.id, x.label, lang, entity)}</option>
                          ))}
                        </NativeSelect>
                        <div className="flex items-center">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("moveUp")} onClick={() => shift(s.id, fi, -1)} disabled={fi === 0}>
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title={tx("moveDown")} onClick={() => shift(s.id, fi, 1)} disabled={fi === s.fields.length - 1}>
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title={isLocked ? tx("lockedField") : tx("removeField")} onClick={() => removeField(f.key)}
                            disabled={isLocked} data-testid={`designer-remove-${f.key}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {available.length > 0 && (
                  <div className="border-t px-3 py-2">
                    <NativeSelect value="" onChange={(e) => { if (e.target.value) moveField(e.target.value, s.id); }} className="h-8 w-56 text-xs"
                      data-testid={`designer-add-${s.id}`}>
                      <option value="">{tx("addField")}</option>
                      {available.map((f) => <option key={f.key} value={f.key}>{label(f.key)}</option>)}
                    </NativeSelect>
                  </div>
                )}
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={addSection} data-testid="designer-add-section">
                <Plus className="h-4 w-4" /> {tx("addSection")}
              </Button>
              <span className="text-xs text-muted-foreground">{tx("systemSectionNote")}</span>
            </div>
          </div>

          <aside
            className={`h-fit rounded-lg border bg-muted/30 p-3 lg:sticky lg:top-0 ${dropTarget === "available" ? "ring-2 ring-ring" : ""}`}
            onDragOver={allowDrop("available")}
            onDrop={dropOnAvailable}
            data-testid="designer-available"
          >
            <p className="mb-2 text-sm font-medium">{tx("availableFields")}</p>
            {available.length === 0 ? (
              <p className="text-xs text-muted-foreground">{tx("allPlaced")}</p>
            ) : (
              <ul className="space-y-1">
                {available.map((f) => (
                  <li key={f.key} draggable onDragStart={onDragStart(f.key, null)} onDragEnd={() => { drag.current = null; setDropTarget(null); }}
                    className="flex cursor-grab items-center gap-2 rounded-md border bg-background px-2 py-1.5 text-sm" data-testid={`designer-available-${f.key}`}>
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{label(f.key)}</span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>

        {error && <p className="text-sm text-destructive" role="alert" data-testid="designer-error">{error}</p>}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={reset} disabled={saving || !isCustom} data-testid="designer-reset">
            <RotateCcw className="h-4 w-4" /> {tx("resetDefault")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>{tx("cancel")}</Button>
            <Button onClick={save} disabled={saving} data-testid="designer-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {tx("save")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
